import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Users, Plus, Edit2, Trash2, ChevronRight } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useSupabaseQuery, useSupabaseInsert, useSupabaseUpdate, useSupabaseDelete } from '../hooks/useSupabase'
import { Button, Card, Input, Select, Modal, EmptyState, PageHeader, Badge, Toast } from '../components/ui/SharedUI'

const PROGRAMS = [
  { value: '', label: 'Select program...' },
  { value: 'icwp', label: 'ICWP' },
  { value: 'ccsp', label: 'CCSP' },
  { value: 'source', label: 'SOURCE' },
  { value: 'edwp', label: 'EDWP' },
  { value: 'sfc', label: 'SFC' },
  { value: 'private_pay', label: 'Private pay' },
  { value: 'other', label: 'Other' },
]

const PROGRAM_LABELS = {
  icwp: 'ICWP', ccsp: 'CCSP', source: 'SOURCE', edwp: 'EDWP',
  sfc: 'SFC', private_pay: 'Private pay', other: 'Other',
}

export function RecipientsPage() {
  const { user } = useAuth()
  const { data: recipients, loading, refetch } = useSupabaseQuery('recipients', { column: 'owner_id' })
  const { insert } = useSupabaseInsert('recipients')
  const { update } = useSupabaseUpdate('recipients')
  const { remove } = useSupabaseDelete('recipients')

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [toast, setToast] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const [form, setForm] = useState({ name: '', relationship: '', program: '', agency_name: '', date_of_birth: '' })

  function openAdd() {
    setEditing(null)
    setForm({ name: '', relationship: '', program: '', agency_name: '', date_of_birth: '' })
    setModalOpen(true)
  }

  function openEdit(r) {
    setEditing(r)
    setForm({
      name: r.name || '',
      relationship: r.relationship || '',
      program: r.program || '',
      agency_name: r.agency_name || '',
      date_of_birth: r.date_of_birth || '',
    })
    setModalOpen(true)
  }

  async function handleSubmit(e) {
    e.preventDefault()
    try {
      if (editing) {
        await update(editing.id, { ...form, program: form.program || null })
        setToast({ message: 'Recipient updated', type: 'success' })
      } else {
        await insert({ ...form, program: form.program || null, owner_id: user.id })
        setToast({ message: 'Recipient added', type: 'success' })
      }
      setModalOpen(false)
      refetch()
    } catch (err) {
      setToast({ message: err.message, type: 'error' })
    }
  }

  async function handleDelete(id) {
    try {
      await remove(id)
      setDeleteConfirm(null)
      setToast({ message: 'Recipient removed', type: 'success' })
      refetch()
    } catch (err) {
      setToast({ message: err.message, type: 'error' })
    }
  }

  if (loading) return <div style={{ padding: '2rem', color: '#718096' }}>Loading...</div>

  return (
    <div style={{ padding: '2rem', maxWidth: '900px' }}>
      <PageHeader
        title="Care recipients"
        description="People you provide care for. Each recipient is context for your shift logs."
        action={<Button onClick={openAdd}><Plus size={16} /> Add recipient</Button>}
      />

      {recipients.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No recipients yet"
          description="Add the person you care for. This is who your shifts are logged under."
          action={<Button onClick={openAdd}><Plus size={16} /> Add your first recipient</Button>}
        />
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {recipients.map(r => (
            <Card key={r.id} style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Link to={`/recipients/${r.id}`} style={{
                  flex: 1, padding: '16px', textDecoration: 'none', color: 'inherit',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px',
                }}>
                  <div>
                    <div style={{ fontSize: '16px', fontWeight: 500, color: '#1A202C', marginBottom: '4px' }}>
                      {r.name}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {r.relationship && (
                        <span style={{ fontSize: '13px', color: '#718096' }}>{r.relationship}</span>
                      )}
                      {r.program && <Badge>{PROGRAM_LABELS[r.program] || r.program}</Badge>}
                      {r.agency_name && (
                        <span style={{ fontSize: '13px', color: '#718096' }}>via {r.agency_name}</span>
                      )}
                    </div>
                  </div>
                  <ChevronRight size={18} style={{ color: '#A0AEC0' }} />
                </Link>
                <div style={{ display: 'flex', gap: '4px', paddingRight: '12px' }}>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(r)}><Edit2 size={15} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(r)}><Trash2 size={15} /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit recipient' : 'Add a care recipient'}>
        <form onSubmit={handleSubmit}>
          <Input
            label="Name"
            id="name"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            required
            placeholder="e.g. Mom, Dad, Mrs. Johnson"
          />
          <Input
            label="Your relationship"
            id="relationship"
            value={form.relationship}
            onChange={e => setForm({ ...form, relationship: e.target.value })}
            placeholder="e.g. Daughter, Paid aide, Family caregiver"
          />
          <Select
            label="Program"
            id="program"
            value={form.program}
            onChange={e => setForm({ ...form, program: e.target.value })}
            options={PROGRAMS}
          />
          <Input
            label="Agency (if applicable)"
            id="agency_name"
            value={form.agency_name}
            onChange={e => setForm({ ...form, agency_name: e.target.value })}
            placeholder="e.g. Serenity Health Care Services"
          />
          <Input
            label="Date of birth"
            id="date_of_birth"
            type="date"
            value={form.date_of_birth}
            onChange={e => setForm({ ...form, date_of_birth: e.target.value })}
          />
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit">{editing ? 'Save changes' : 'Add recipient'}</Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Remove recipient?" width={400}>
        <p style={{ color: '#4A5568', marginBottom: '1rem', lineHeight: 1.6 }}>
          This will remove <strong>{deleteConfirm?.name}</strong> from your recipients. Shifts already logged for them won't be deleted, but they'll no longer be linked to a recipient.
        </p>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button variant="danger" onClick={() => handleDelete(deleteConfirm.id)}>Remove</Button>
        </div>
      </Modal>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  )
}
